import React from 'react'
import { Box, FormControl, InputLabel, ListSubheader, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import useThemeMode from '../../hooks/useThemeMode'

export interface FilterOption {
  id: string
  name: string
  /** Options sharing a group render indented under a subheader, after the ungrouped ones. */
  group?: string
  /** Shown faint at the right of the option. */
  count?: number
  /** Label in the closed select when it differs from the option text, e.g. "Trial - converted". */
  selectedName?: string
}

interface FilterSelectProps {
  label: string
  value: string
  options: FilterOption[]
  onChange: (value: string) => void
  includeAll?: boolean
}

const FilterSelect = ({ label, value, options, onChange, includeAll = true }: FilterSelectProps) => {
  const { isDark } = useThemeMode()

  const handleChange = (e: SelectChangeEvent) => {
    onChange(e.target.value)
  }

  const grouped = options.some((o) => o.group)
  const item = (option: FilterOption) => (
    <MenuItem
      key={option.id}
      value={option.id}
      sx={grouped ? { pl: option.group ? 3 : 2, justifyContent: 'space-between', gap: 2 } : undefined}
    >
      {option.name}
      {option.count != null && (
        <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
          {option.count}
        </Box>
      )}
    </MenuItem>
  )
  // MUI Select needs its items as direct children, so subheaders go into the same flat list.
  const items = grouped
    ? [
        ...options.filter((o) => !o.group).map(item),
        ...Array.from(new Set(options.filter((o) => o.group).map((o) => o.group))).flatMap((g) => [
          <ListSubheader
            key={`group-${g}`}
            sx={{ fontSize: '11px', fontWeight: 600, lineHeight: '28px', color: 'text.secondary', bgcolor: 'inherit' }}
          >
            {g}
          </ListSubheader>,
          ...options.filter((o) => o.group === g).map(item),
        ]),
      ]
    : options.map(item)
  const renderValue = grouped
    ? (v: string) => {
        const o = options.find((x) => x.id === v)
        return o ? o.selectedName ?? o.name : 'All'
      }
    : undefined

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: '100%', sm: 180 },
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          fontSize: '14px',
          bgcolor: 'background.paper',
          '& fieldset': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
          },
          '&:hover fieldset': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: '14px',
        },
      }}
    >
      <InputLabel>{label}</InputLabel>
      <Select onChange={handleChange} label={label} value={value} renderValue={renderValue}>
        {includeAll && <MenuItem value="all">All</MenuItem>}
        {items}
      </Select>
    </FormControl>
  )
}

export default FilterSelect
